import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../../contexts/ProfileContext';
import { supabase } from '../../../utils/supabaseClient';
import { hardResetAuth } from '../../../utils/supabaseAuthSafe';
import './GenerationsMobileHeaderMenu.css';

/**
 * Mobile-only header menu for Generations.
 * Combines "Info" + "Account" into a single cog button with a dropdown.
 */
const GenerationsMobileHeaderMenu = ({ onInfo }) => {
  const { user, profile } = useProfile();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const displayName = useMemo(() => {
    return profile?.username || user?.email || 'Account';
  }, [profile?.username, user?.email]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const handleInfo = () => {
    close();
    if (typeof onInfo === 'function') onInfo();
  };

  const handleSignIn = () => {
    close();
    navigate('/auth');
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await hardResetAuth(supabase, { revokeRemote: true, remoteTimeoutMs: 4000 });
    } catch (error) {
      console.error('Sign out failed:', error?.message || error);
    } finally {
      setIsSigningOut(false);
      close();
      navigate('/');
    }
  };

  return (
    <div className="gen-mobile-header-menu" ref={menuRef}>
      <button
        type="button"
        className="gen-mobile-header-menu-button header-pill-button coin-display-link"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Menu"
        aria-haspopup="true"
        aria-expanded={isOpen}
        title="Menu"
      >
        <FaCog className="gen-mobile-header-menu-icon" aria-hidden="true" focusable="false" />
      </button>

      {isOpen && (
        <div className="gen-mobile-header-menu-dropdown" role="menu">
          <button
            type="button"
            className="gen-mobile-header-menu-item"
            onClick={handleInfo}
            role="menuitem"
          >
            Info
          </button>

          <div className="gen-mobile-header-menu-divider" />

          <div className="gen-mobile-header-menu-account">
            <div className="gen-mobile-header-menu-account-name">{displayName}</div>
            <div className="gen-mobile-header-menu-account-subtitle">
              {user ? 'Signed in' : 'Guest'}
            </div>
          </div>

          {user ? (
            <button
              type="button"
              className="gen-mobile-header-menu-item gen-mobile-header-menu-danger"
              onClick={handleSignOut}
              role="menuitem"
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing out...' : 'Sign out'}
            </button>
          ) : (
            <button
              type="button"
              className="gen-mobile-header-menu-item"
              onClick={handleSignIn}
              role="menuitem"
            >
              Sign in / Sign up
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GenerationsMobileHeaderMenu;
